import { toRFC3339 } from "../utils/time";
import { fetchJson } from "./http";
import type { GraphAggregation, Metric, ServiceSlug } from "./types";

export interface GetMetricsParams {
  serviceSlug: ServiceSlug;
  serviceName: string;
  serviceInstance?: string;
  metricName?: string;
  startTime: Date;
  endTime: Date;
  limit?: number;
  signal?: AbortSignal;
}

export interface GetMetricsResult {
  data: Metric[];
  aggregation: GraphAggregation;
}

interface MetricsGraphResponse {
  data?: Metric[] | null;
  aggregation?: GraphAggregation;
  timespan?: { from: string; to: string };
}

export async function getMetrics({
  serviceSlug,
  serviceName,
  serviceInstance,
  metricName,
  startTime,
  endTime,
  limit,
  signal,
}: GetMetricsParams): Promise<GetMetricsResult> {
  const raw = await fetchJson<MetricsGraphResponse | Metric[] | null>("/metrics", {
    serviceSlug,
    signal,
    searchParams: {
      service_name: serviceName,
      service_instance: serviceInstance,
      metric_name: metricName,
      start_time: toRFC3339(startTime),
      end_time: toRFC3339(endTime),
      limit,
    },
  });
  // Support unified body or legacy array response
  if (raw && typeof raw === "object" && "data" in raw && "aggregation" in raw) {
    const body = raw as MetricsGraphResponse;
    return {
      data: Array.isArray(body.data) ? body.data : [],
      aggregation: body.aggregation ?? { type: "none" },
    };
  }
  return {
    data: Array.isArray(raw) ? raw : [],
    aggregation: { type: "none" },
  };
}

export interface GetMetricsForSpanParams {
  serviceSlug: ServiceSlug;
  traceId: string;
  spanId: string;
  metricName?: string;
  limit?: number;
  signal?: AbortSignal;
}

export async function getMetricsForSpan({
  serviceSlug,
  traceId,
  spanId,
  metricName,
  limit,
  signal,
}: GetMetricsForSpanParams): Promise<Metric[]> {
  const result = await fetchJson<Metric[] | null>(
    `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/metrics`,
    {
      serviceSlug,
      signal,
      searchParams: {
        metric_name: metricName,
        limit,
      },
    },
  );
  return Array.isArray(result) ? result : [];
}
